import { PAGINATION_CONSTS } from '@app/consts';

export enum EllipsisType {
    START = 'ellipsis-start',
    END = 'ellipsis-end',
}

export type PageButton = number | EllipsisType;

/**
 * Calculates which page buttons should be visible in the pagination component.
 *
 * Logic:
 * - If total pages <= 5: Show all pages [1, 2, 3, 4, 5]
 * - On last page: Show [1, ..., pageCount-2, pageCount-1, pageCount]
 * - Otherwise: Show current page with neighbors and ellipsis at end
 * - Never show both ellipsis at once
 *
 * Examples:
 * - Page 1 of 25: [1, 2, 3, ..., 25]
 * - Page 7 of 25: [6, 7, 8, ..., 25]
 * - Page 25 of 25: [1, ..., 23, 24, 25]
 *
 * @param currentPage - Current active page (1-indexed)
 * @param pageCount - Total number of pages
 * @returns Array of page numbers and ellipsis indicators
 */
export const calculateVisiblePages = (currentPage: number, pageCount: number): PageButton[] => {
    const { MAX_VISIBLE_PAGES } = PAGINATION_CONSTS;

    // For small page counts, show all pages
    if (pageCount <= MAX_VISIBLE_PAGES) {
        return Array.from({ length: pageCount }, (_, i) => i + 1);
    }

    const pages: PageButton[] = [];

    // Special case: on the last page, show ellipsis at start
    if (currentPage === pageCount) {
        pages.push(1);
        pages.push(EllipsisType.START);
        pages.push(pageCount - 2);
        pages.push(pageCount - 1);
        pages.push(pageCount);
        return pages;
    }

    // Calculate visible page range: always show 3 pages around current
    // (current page with 1 neighbor on each side when possible)
    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(pageCount - 1, currentPage + 1);

    // If at the beginning, ensure we show 3 pages
    if (startPage === 1 && endPage < 3) {
        endPage = Math.min(pageCount - 1, 3);
    }

    // If at the end (but not last page), ensure we show 3 pages
    if (endPage === pageCount - 1 && startPage > pageCount - 3) {
        startPage = Math.max(1, pageCount - 3);
    }

    // Add the visible pages
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    // Add ellipsis and last page if needed
    if (endPage < pageCount) {
        // Always show ellipsis when there's a gap (pageCount > 5 means we always have ellipsis)
        pages.push(EllipsisType.END);
        pages.push(pageCount);
    }

    return pages;
};

/**
 * Validates a page number input.
 *
 * @param value - String value from input
 * @param pageCount - Total number of pages
 * @returns Valid page number or null if invalid
 */
export const validatePageNumber = (value: string, pageCount: number): number | null => {
    if (!value || value.trim() === '') {
        return null;
    }

    const pageNumber = parseInt(value, 10);

    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > pageCount) {
        return null;
    }

    return pageNumber;
};
